import React, { useState, useEffect } from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { Box, Flex, Text } from "rebass";
import {
  Card,
  TextContainer,
  TextStyle,
  Collapsible,
  ResourceList,
  EmptyState,
  Link,
  ChoiceList,
  Filters,
  Pagination,
  ResourceItem,
  Subheading,
  Icon,
  Tabs,
  Button,
} from "@shopify/polaris";
import { parseISO, formatDistanceToNow } from "date-fns";
// import { MiniIcon } from "components/checks/new";
import {
  PhoneMajor,
  EmailMajor,
  CircleCancelMajor,
} from "@shopify/polaris-icons";
import useDebounce from "hooks/useDebounce";
import CustomerSelection from "./CustomerSelection";
import * as Sentry from "@sentry/react";
import { createCheck, searchOrders } from "services/wpApi";
import { useFormikContext } from "formik";
import { useToast } from "providers/ToastProvider";
import cx from "classnames";
import startCase from "lodash/startCase";
import Currency from "../core/Currency";

export default function OrderSelectionV2({
  setOpenSearchOrders,
  openSearchOrders,
  setStep,
}) {
  const { values, resetForm, setFieldValue } = useFormikContext();
  const setToast = useToast();
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [orderNameQuery, setOrderNameQuery] = useState("");
  const debouncedOrderNameQuery = useDebounce(orderNameQuery, 500);

  const [isShortcodeHelpOpen, setIsShortCodeHelpOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(0);
  const [orders, setOrders] = useState([]);

  const tabs = [
    {
      id: "orders",
      content: "Orders",
      accessibilityLabel: "Recent orders",
      panelID: "orders",
    },
    {
      id: "customers",
      content: "Customers",
      accessibilityLabel: "Recent customers",
      panelID: "customers",
    },
  ];

  /**
   * Search for recent orders
   */
  useEffect(() => {
    // https://github.com/woocommerce/woocommerce/wiki/wc_get_orders-and-WC_Order_Query#parameters
    searchOrders({
      // https://github.com/woocommerce/woocommerce/wiki/wc_get_orders-and-WC_Order_Query#customer
      customer: debouncedOrderNameQuery,
      page,
      limit: 3,
      orderby: "ID",
      order: "DESC",
    })
      .then(({ data: { orders, max_num_pages, total } }) => {
        setOrders(orders);
        setTotalPages(max_num_pages);
      })
      .catch((err) => {
        console.log(err);
        setToast("Unable to search your orders. Please contact support.");
      });
  }, [debouncedOrderNameQuery, page]);

  return (
    <>
      <p className="ri-text-gray-700 ri-mb-2 ri-px-5">
        To start a new ID check, select a recent order below, or search for a
        specific order by the customers email address or ID number.
      </p>
      <p className="ri-text-gray-700 ri-px-5">
        You can also{" "}
        <Link
          onClick={() => {
            resetForm();
            setStep(2);
          }}
        >
          create a check from scratch.
        </Link>
      </p>
      <Collapsible
        open={openSearchOrders}
        id="orders-search-collapsible"
        transition={{
          duration: "500ms",
          timingFunction: "ease-in-out",
        }}
      >
        <Tabs tabs={tabs} selected={selectedMenu} onSelect={setSelectedMenu}>
          {selectedMenu === 0 && (
            <>
              <ResourceList
                items={orders || []}
                totalItemsCount={orders?.length || 0}
                filterControl={
                  <Filters
                    filters={
                      [
                        // {
                        //   key: "orderStatus",
                        //   label: "Status",
                        //   filter: (
                        //     <ChoiceList
                        //       title="Fulfillment Status"
                        //       titleHidden
                        //       choices={[
                        //         { label: "Medium", value: "MEDIUM" },
                        //         { label: "High", value: "HIGH" },
                        //       ]}
                        //       selected={orderRiskLevels || []}
                        //       onChange={(levels) => setOrderRiskLevels(levels)}
                        //       allowMultiple
                        //     />
                        //   ),
                        //   shortcut: true,
                        //   hideClearButton: true,
                        //   clearButton: false,
                        // },
                      ]
                    }
                    appliedFilters={[]}
                    onClearAll={() => {}}
                    queryValue={orderNameQuery}
                    onQueryChange={setOrderNameQuery}
                    onQueryClear={() => {
                      setOrderNameQuery("");
                    }}
                    totalItemsCount={orders?.length || 0}
                  />
                }
                resourceName={{
                  singular: "order",
                  plural: "orders",
                }}
                emptyState={
                  <EmptyState
                    heading="No orders found"
                    secondaryAction={{
                      content: "Skip order selection",
                      onClick: () => {
                        // TODO: empty state should just go to the create from scratch by default?
                        resetForm();
                        setStep(2);
                      },
                    }}
                  >
                    No orders made in the past 60 days found
                    {orderNameQuery ? ` with the ID #${orderNameQuery}` : ""}.
                  </EmptyState>
                }
                renderItem={(order) => (
                  <ResourceItem
                    key={order.id}
                    onClick={() => {
                      if (values.wcOrderId && values.wcOrderId == order.id) {
                        resetForm();
                        setToast(`Reset ID check customer & order details.`);
                        return;
                      }

                      const phone =
                        order?.customer?.phone || order?.billing?.phone;

                      const email =
                        order?.customer?.email || order?.billing?.email;

                      setFieldValue(
                        "firstName",
                        order?.customer?.first_name ||
                          order?.billing?.first_name
                      );
                      setFieldValue(
                        "lastName",
                        order?.customer?.last_name || order?.billing?.last_name
                      );
                      setFieldValue("email", email);
                      if (phone) setFieldValue("phone", phone);
                      let deliveryMethods = [];
                      if (email) {
                        deliveryMethods.push("email");
                      }

                      if (phone) deliveryMethods.push("sms");
                      setFieldValue("deliveryMethods", deliveryMethods);

                      setFieldValue("wcOrderId", order.id);
                      setFieldValue("orderId", order.id);
                      setFieldValue("wcCustomerId", order.customer_id);
                      //   setRecentOrders(
                      //     orders.filter((o) => o.id == order.id)
                      //   );
                      setToast(
                        `Using customer and order details from order #${order.id} for the ID check.`
                      );
                      setStep(3);
                    }}
                  >
                    <div
                      key={order.id}
                      className={cx(
                        "ri-flex ri-justify-between ri-my-3 ri-px-6 ri-py-8 ri-space-y-4"
                      )}
                    >
                      <div className="ri-flex-col">
                        <h3 className="ri-text-lg ri-text-grey-800 ri-my-2">
                          #{order.id}
                        </h3>
                        <p></p>
                        <p className="ri-text-grey-600">
                          {order.billing?.first_name} {order.billing?.last_name}
                        </p>
                        <p className="ri-text-blue-600 ri-underline">
                          {order.billing?.email}
                        </p>
                      </div>
                      <div className="ri-flex-col">
                        <div className="ri-p-3 ri-rounded ri-bg-gray-100 ri-border-1 ri-border-gray-200">
                          {startCase(order.status.replace("-", " "))}
                        </div>
                      </div>
                      <div className="ri-flex-col">
                        <div className="">
                          {formatDistanceToNow(
                            parseISO(order.date_created?.date)
                          )}{" "}
                          ago
                        </div>
                      </div>
                      <div className="">
                        <Currency
                          amount={order.total}
                          currencyCode={order.currency}
                        />
                      </div>
                    </div>
                  </ResourceItem>
                )}
              />
              {totalPages > 1 && (
                <>
                  <Box my={2}>
                    {page} / {totalPages}
                  </Box>
                  <Pagination
                    hasPrevious={page - 1 >= 1}
                    onPrevious={() => {
                      setPage(page - 1);
                    }}
                    hasNext={page + 1 <= totalPages}
                    onNext={() => {
                      setPage(page + 1);
                    }}
                  />
                </>
              )}
            </>
          )}
          {selectedMenu === 1 && (
            <CustomerSelection
              setOpenSearchOrders={setOpenSearchOrders}
              setStep={setStep}
            />
          )}
        </Tabs>
      </Collapsible>
    </>
  );
}
